import cn, { Argument as ClassName } from 'classnames'
import { classNames } from 'primereact/utils'
import React, { ComponentPropsWithRef, HTMLAttributes, HTMLProps, ReactElement } from 'react'

import Text from '../../ui/Text'

import { Wrapper, STable, THead, SStyledRow } from './Table.styles'

export type TableProps = {
    children: React.ReactNode
    style?: React.CSSProperties
    isLoading?: boolean
    dataTestId?: string
} & HTMLAttributes<HTMLDivElement>

export function Table({ children, style, isLoading, dataTestId, ...props }: TableProps): React.ReactElement {
    return (
        <Wrapper style={style} {...props}>
            <STable
                className={classNames('table table-responsive', { 'table-hover': !isLoading })}
                data-testid={dataTestId ?? 'table'}
            >
                {children}
            </STable>
        </Wrapper>
    )
}

export type TableHeaderProps = {
    children: React.ReactNode
    className?: ClassName
    style?: React.CSSProperties
}

export function TableHeader({ children, className, style }: TableHeaderProps): React.ReactElement {
    return (
        <THead className={cn(className)} style={style}>
            {children}
        </THead>
    )
}

export type TableGeneralProps = {
    children?: React.ReactNode
    className?: ClassName
    dataTestId?: string
    colspan?: number
}

interface ITableRowProps extends ComponentPropsWithRef<'tr'> {
    clickable?: boolean
    'data-testid'?: string
}

export function TableRow({
    children,
    className,
    'data-testid': dataTestId,
    ...rest
}: ITableRowProps): React.ReactElement {
    return (
        <SStyledRow data-testid={dataTestId ?? 'table-row'} {...(className && { className: cn(className) })} {...rest}>
            {children}
        </SStyledRow>
    )
}

export function TableHeaderCell({
    children,
    className,
    ...rest
}: HTMLAttributes<HTMLTableCellElement>): React.ReactElement {
    return (
        <th
            data-testid={rest['data-testid'] ?? 'table-header-cell'}
            {...(className && { className: cn(className) })}
            {...rest}
        >
            {children}
        </th>
    )
}

type TableBodyProps = {
    isLoading?: boolean
    numberOfLoadingItems?: number
    numberOfLoadingColumns?: number
} & TableGeneralProps

export function TableBody({
    children,
    dataTestId,
    isLoading,
    numberOfLoadingItems = 7,
    numberOfLoadingColumns = 4,
}: TableBodyProps): React.ReactElement {
    return (
        <tbody data-testid={dataTestId ?? 'table-body'}>
            {isLoading
                ? Array(numberOfLoadingItems)
                      .fill(true)
                      .map((_, index) => <LoadingTableRow key={index} items={numberOfLoadingColumns} />)
                : children}
        </tbody>
    )
}

export function TableBodyCell({ children, ...rest }: HTMLProps<HTMLTableCellElement>): React.ReactElement {
    return (
        <td data-testid={rest['data-testid'] ?? 'table-body-cell'} {...rest}>
            {children}
        </td>
    )
}

export const LoadingTableRow = ({ items = 4 }): ReactElement => (
    <TableRow>
        {Array(items)
            .fill(true)
            .map((_, index) => (
                <TableBodyCell key={`loader-${index}`}>
                    <div className="shimmer-container w-full h-3rem border-round-3xl" data-testid={`loader-${index}`} />
                </TableBodyCell>
            ))}
    </TableRow>
)

type SortHeaderProps = {
    code: string
    id: 'name' | 'description' | 'email' | 'phone' | 'partnerName'
    sortType: string
    descending: boolean
    onClick: (sortType: 'name' | 'description' | 'email' | 'phone' | 'partnerName', descending: boolean) => void
}

export function SortHeader({ code, id, sortType, descending, onClick }: SortHeaderProps): React.ReactElement {
    return (
        <a className="pointer" onClick={() => onClick(id, !descending)} data-testid={id}>
            <Text code={code} />
            {sortType === id && <span className={`fa fa-caret-${!descending ? 'down' : 'up'}`}></span>}
        </a>
    )
}
